/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  InputAdornment,
  Switch,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PaletteIcon from "@material-ui/icons/Palette";
import SearchIcon from "@material-ui/icons/Search";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import BrightnessAutoIcon from "@material-ui/icons/BrightnessAuto";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import ThemePreviewMock from "./ThemePreviewMock";
import {
  STRINGS,
  MODIFIER_KEY,
  THEME_OPTIONS,
  MODES,
  applyTheme,
  getStoredTheme,
  getStoredMode,
  getNextMode,
  cycleMode,
  getResolvedMode,
  applySidebarVibrant,
  getStoredSidebarVibrant,
} from "./M_ThemeSelector.helper";
import "./M_ThemeSelector.css";

const MODE_ICONS = {
  [MODES.LIGHT]: Brightness7Icon,
  [MODES.DARK]: Brightness4Icon,
  [MODES.SYSTEM]: BrightnessAutoIcon,
};

const M_ThemeSelector = (props) => {
  const { onThemeChange } = props;
  const [activeTheme, setActiveTheme] = useState(getStoredTheme());
  const [mode, setMode] = useState(getStoredMode());
  // Tracked as state (not derived from `mode`) so an OS-level flip while
  // mode is "system" still re-renders the preview mocks
  const [isDarkMode, setIsDarkMode] = useState(
    getResolvedMode() === MODES.DARK
  );
  const [query, setQuery] = useState("");
  const [spin, setSpin] = useState(0);
  const [sidebarVibrant, setSidebarVibrant] = useState(
    getStoredSidebarVibrant()
  );
  const ModeIcon = MODE_ICONS[mode] || Brightness4Icon;
  const nextModeLabel = STRINGS.MODE_LABELS[getNextMode(mode)];

  useEffect(() => {
    setActiveTheme(getStoredTheme());
    setMode(getStoredMode());
    setIsDarkMode(getResolvedMode() === MODES.DARK);
    setSidebarVibrant(getStoredSidebarVibrant());

    const handleGlobalThemeChange = (event) => {
      const { theme, mode: nextMode } = event.detail || {};
      if (theme) {
        setActiveTheme(theme);
      }
      if (nextMode) {
        setMode(nextMode);
      }
      setIsDarkMode(getResolvedMode() === MODES.DARK);
      setSidebarVibrant(getStoredSidebarVibrant());
    };

    window.addEventListener("themeChange", handleGlobalThemeChange);
    return () => window.removeEventListener("themeChange", handleGlobalThemeChange);
  }, []);

  const handleCycleMode = (event) => {
    setSpin((value) => value + 1);
    setMode(cycleMode({ x: event.clientX, y: event.clientY }));
  };

  const handleVibrantToggle = (event) => {
    const next = event.target.checked;
    setSidebarVibrant(next);
    applySidebarVibrant(next);
  };

  const handleSelect = (themeId, event) => {
    if (!themeId || themeId === activeTheme) {
      return;
    }
    // Keyboard events carry no clientX/Y — fall back to the default wipe origin
    const coords =
      event && typeof event.clientX === "number"
        ? { x: event.clientX, y: event.clientY }
        : null;
    setActiveTheme(themeId);
    applyTheme(themeId, coords);
    if (typeof onThemeChange === "function") {
      onThemeChange(themeId);
    }
  };

  const { matchCount, themes } = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const matches = trimmed
      ? THEME_OPTIONS.filter(
          (theme) =>
            theme.label.toLowerCase().includes(trimmed) ||
            theme.value.toLowerCase().includes(trimmed) ||
            (theme.group || "").toLowerCase().includes(trimmed)
        )
      : THEME_OPTIONS;
    return { matchCount: matches.length, themes: matches };
  }, [query]);

  return (
    <div className="m-theme-selector">
      <div className="m-theme-selector__hero">
        <div className="m-theme-selector__hero-copy">
          <Typography className="m-theme-selector__hero-title">
            {STRINGS.HERO_TITLE}
          </Typography>
          <Typography className="m-theme-selector__hero-subtitle">
            {STRINGS.HERO_SUBTITLE}
          </Typography>
        </div>
        <button
          type="button"
          className="m-theme-selector__mode-toggle"
          onClick={handleCycleMode}
          aria-label={STRINGS.modeSwitchAria(nextModeLabel)}
          title={STRINGS.modeSwitchAria(nextModeLabel)}
        >
          <ModeIcon />
          <span>{STRINGS.MODE_LABELS[mode] || STRINGS.MODE_LABELS[MODES.LIGHT]}</span>
          <AutorenewIcon
            key={spin}
            className={
              "m-theme-selector__mode-cycle" +
              (spin > 0 ? " m-theme-selector__mode-cycle--spin" : "")
            }
          />
        </button>
      </div>

      <div className="m-theme-selector__option">
        <div className="m-theme-selector__option-copy">
          <Typography className="m-theme-selector__option-title">
            Vibrant sidebar
          </Typography>
          <Typography className="m-theme-selector__option-desc">
            Accent-tinted gradient treatment for the side menu. Turn off for the classic flat look.
          </Typography>
        </div>
        <Switch
          color="primary"
          checked={sidebarVibrant}
          onChange={handleVibrantToggle}
          inputProps={{ "aria-label": "Toggle vibrant sidebar" }}
        />
      </div>

      <div className="m-theme-selector__toolbar">
        <div className="m-theme-selector__search">
          <A_TextField
            variant="outlined"
            label=""
            placeholder={STRINGS.SEARCH_PLACEHOLDER}
            defaultValue={query}
            inputType="value"
            fullWidth
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="m-theme-selector__shortcuts">
          <span className="m-theme-selector__shortcut">
            <span className="m-theme-selector__kbd">{MODIFIER_KEY}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.PREV}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.NEXT}</span>
            {STRINGS.SHORTCUTS.SWITCH_THEME}
          </span>
          <span className="m-theme-selector__shortcut">
            <span className="m-theme-selector__kbd">{MODIFIER_KEY}</span>
            <span className="m-theme-selector__kbd">{STRINGS.KEYS.MODE}</span>
            {STRINGS.SHORTCUTS.TOGGLE_MODE}
          </span>
        </div>
      </div>

      {matchCount === 0 ? (
        <div className="m-theme-selector__empty">
          <Typography>{STRINGS.noMatchMessage(query)}</Typography>
        </div>
      ) : (
        <div role="radiogroup" aria-label={STRINGS.RADIOGROUP_LABEL}>
          <Grid container spacing={2} className="m-theme-selector__grid">
            {themes.map((theme) => {
              const isActive = activeTheme === theme.value;
              const cardClassName =
                "m-theme-selector__card" +
                (isActive ? " m-theme-selector__card--active" : "");

              return (
                <Grid item xs={12} sm={6} md={4} key={theme.value}>
                  <Box
                    className={cardClassName}
                    onClick={(event) => handleSelect(theme.value, event)}
                    role="radio"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleSelect(theme.value, event);
                      }
                    }}
                    aria-checked={isActive}
                    aria-label={theme.label}
                  >
                    <ThemePreviewMock
                      themeId={theme.value}
                      isDark={isDarkMode}
                    />

                    <div className="m-theme-selector__title-row">
                      <div className="m-theme-selector__title-block">
                        <Typography className="m-theme-selector__title">
                          {theme.label}
                        </Typography>
                        <Typography className="m-theme-selector__meta">
                          {theme.value}
                        </Typography>
                      </div>
                      {isActive ? (
                        <div className="m-theme-selector__active-pill">
                          <CheckCircleIcon
                            className="m-theme-selector__check"
                            fontSize="small"
                          />
                          <span>{STRINGS.ACTIVE_LABEL}</span>
                        </div>
                      ) : null}
                    </div>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </div>
      )}
    </div>
  );
};

M_ThemeSelector.propTypes = {
  onThemeChange: PropTypes.func,
};

export default M_ThemeSelector;
